import { memo } from 'react';

import { BoardThemes, ChessBoard } from '@exports/ChessBoard';
import { FEN_STARTING_POSITION } from '@libs/chess/constants';
import { IPgnMove, Pgn } from '@libs/chess/pgn';
import { cx } from '@libs/classnames';

import styles from './GamesSelector.module.css';

function GameSelectorPlayer({
  name,
  result,
  color,
}: {
  name?: string;
  result?: '1-0' | '0-1' | '1/2-1/2';
  color: 'white' | 'black';
}) {
  const score = formatScore(color, result);

  return (
    <div className={styles.games__player} title={name}>
      <span className={styles.games__player_name}>{name ?? '?'}</span>
      {score && <span className={styles.games__player_score}>{score}</span>}
    </div>
  );
}

export function GameSelector({
  games,
  selectedIndex,
  theme,
  onSelect,
}: {
  games: Pgn<IPgnMove>[];
  selectedIndex: number;
  theme?: BoardThemes;
  onSelect?: (pgn: Pgn<IPgnMove>, index: number) => void;
}) {
  return (
    <div className={styles.games}>
      {games.map((pgn, i) => (
        <GameSelectorItem
          key={i}
          pgn={pgn}
          theme={theme}
          index={i}
          onSelect={onSelect}
          selected={selectedIndex === i}
        />
      ))}
    </div>
  );
}

const GameSelectorItem = memo(function GameSelectorItem({
  pgn,
  selected,
  index,
  theme,
  onSelect,
}: {
  pgn: Pgn<IPgnMove>;
  selected?: boolean;
  index: number;
  theme?: BoardThemes;
  onSelect?: (pgn: Pgn<IPgnMove>, index: number) => void;
}) {
  return (
    <button
      className={cx(styles.games__item, {
        [styles.is_selected]: selected,
      })}
      onClick={() => onSelect?.(pgn, index)}
    >
      <GameSelectorPlayer name={pgn.black ?? pgn.chapterName} result={pgn.result} color="black" />
      <ChessBoard
        className={styles.games__board}
        orientation="white"
        position={pgn.fen ?? FEN_STARTING_POSITION}
        theme={theme}
        notation="none"
        noShadow
        noBorderRadius
      />
      <GameSelectorPlayer name={pgn.white ?? pgn.studyName} result={pgn.result} color="white" />
    </button>
  );
});

function formatScore(color: 'white' | 'black', result?: '1-0' | '0-1' | '1/2-1/2') {
  return result === '1-0'
    ? color === 'white'
      ? '1'
      : '0'
    : result === '0-1'
      ? color === 'black'
        ? '1'
        : '0'
      : result === '1/2-1/2'
        ? '½'
        : undefined;
}
